// src/components/ModuleReader.tsx
import { useCallback, useMemo as useMemo3, useState as useState4 } from "react";
import { Box as Box4, Button as Button2, Chip, CircularProgress, Divider, Stack as Stack2, Tab, Tabs, Typography as Typography4 } from "@mui/material";
import { BiDownload, BiPlayCircle, BiBookOpen, BiFile, BiCheckCircle as BiCheckCircle3 } from "react-icons/bi";

// src/components/ContentfulMarkdown.tsx
import { useMemo } from "react";
import { css } from "@emotion/css";
import { useTheme } from "@mui/material";

// src/lib/renderMarkdown.ts
import { marked } from "marked";
import DOMPurify from "dompurify";
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName !== "A") return;
  const href = node.getAttribute("href");
  if (!href || href.startsWith("#")) return;
  let isExternal = false;
  try {
    isExternal = new URL(href, window.location.href).origin !== window.location.origin;
  } catch {
    isExternal = false;
  }
  if (isExternal) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});
function slugify(text) {
  const base = text.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  return base || "section";
}
function renderMarkdown(content) {
  const source = content ?? "";
  if (!source.trim()) return { html: "", headings: [] };
  const rawHtml = DOMPurify.sanitize(marked(source));
  if (typeof DOMParser === "undefined") return { html: rawHtml, headings: [] };
  const doc = new DOMParser().parseFromString(rawHtml, "text/html");
  const headings = [];
  const seen = /* @__PURE__ */ new Map();
  doc.querySelectorAll("h1, h2, h3").forEach((el) => {
    const level = Number(el.tagName.charAt(1));
    const text = (el.textContent ?? "").trim();
    const base = slugify(text);
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    const id = count === 0 ? base : `${base}-${count}`;
    el.id = id;
    headings.push({ id, text, level });
  });
  return { html: doc.body.innerHTML, headings };
}

// src/components/ContentfulMarkdown.tsx
import { jsx } from "react/jsx-runtime";
var ContentfulMarkdown = ({
  content,
  html,
  variant = "default"
}) => {
  const theme = useTheme();
  const cls = variant === "modal" ? modalStyles(theme) : variant === "article" ? articleStyles(theme) : defaultStyles(theme);
  const htmlContent = useMemo(
    () => html !== void 0 ? html : renderMarkdown(content).html,
    [html, content]
  );
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cls.markdownContent,
      dangerouslySetInnerHTML: { __html: htmlContent }
    }
  );
};
var defaultStyles = (theme) => ({
  markdownContent: css`
    color: ${theme?.palette?.text?.primary};
    line-height: 1.6;

    h1 {
      font-size: 2rem;
      font-weight: 600;
      margin-bottom: 8px;
      color: ${theme?.palette?.text?.primary};
    }

    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      margin-top: 32px;
      margin-bottom: 16px;
      color: ${theme?.palette?.text?.primary};
    }

    h3 {
      font-size: 1.25rem;
      font-weight: 600;
      margin-top: 24px;
      margin-bottom: 12px;
      color: ${theme?.palette?.text?.primary};
    }

    p {
      margin-bottom: 16px;
      color: ${theme?.palette?.text?.secondary};
    }

    ul,
    ol {
      margin-bottom: 16px;
      padding-left: 24px;
      color: ${theme?.palette?.text?.secondary};
    }

    li {
      margin-bottom: 8px;
    }

    strong {
      font-weight: 600;
      color: ${theme?.palette?.text?.primary};
    }

    hr {
      margin: 24px 0;
      border: none;
      border-top: 1px solid ${theme?.palette?.divider};
    }

    code {
      background-color: ${theme?.palette?.action?.hover};
      padding: 2px 6px;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
    }

    pre {
      background-color: ${theme?.palette?.action?.hover};
      padding: 16px;
      border-radius: 8px;
      overflow-x: auto;
      margin-bottom: 16px;

      code {
        background-color: transparent;
        padding: 0;
      }
    }

    a {
      color: ${theme?.palette?.primary?.main};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    blockquote {
      border-left: 4px solid ${theme?.palette?.primary?.main};
      padding-left: 16px;
      margin: 16px 0;
      color: ${theme?.palette?.text?.secondary};
      font-style: italic;
    }
  `
});
var articleStyles = (theme) => ({
  markdownContent: css`
    color: ${theme?.palette?.text?.primary};
    font-size: 16px;
    line-height: 1.68;

    h1 {
      font-size: 26px;
      font-weight: 700;
      line-height: 1.25;
      margin: 0 0 12px;
      color: ${theme?.palette?.text?.primary};
      scroll-margin-top: 88px;
    }

    h2 {
      font-size: 22px;
      font-weight: 700;
      line-height: 1.3;
      margin: 40px 0 6px;
      color: ${theme?.palette?.text?.primary};
      scroll-margin-top: 88px;
    }

    h2:first-of-type {
      margin-top: 8px;
    }

    h3 {
      font-size: 18px;
      font-weight: 600;
      line-height: 1.35;
      margin: 28px 0 8px;
      color: ${theme?.palette?.text?.primary};
      scroll-margin-top: 88px;
    }

    p {
      margin: 0 0 18px;
    }

    ul,
    ol {
      margin: 0 0 18px;
      padding-left: 24px;
    }

    li {
      margin-bottom: 9px;
    }

    li::marker {
      color: ${theme?.palette?.text?.disabled};
    }

    strong {
      font-weight: 600;
      color: ${theme?.palette?.text?.primary};
    }

    a {
      color: ${theme?.palette?.secondary?.main};
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }

    img {
      display: block;
      max-width: 100%;
      margin: 24px auto;
      border-radius: 8px;
      border: 1px solid ${theme?.palette?.divider};
    }

    hr {
      border: none;
      border-top: 1px solid ${theme?.palette?.divider};
      margin: 32px 0;
    }

    blockquote {
      margin: 22px 0;
      padding: 14px 18px;
      background-color: ${theme?.palette?.action?.hover};
      border: 1px solid ${theme?.palette?.divider};
      border-radius: 10px;

      > :first-of-type {
        margin-top: 0;
      }
      > :last-child {
        margin-bottom: 0;
      }
    }

    code {
      background-color: ${theme?.palette?.action?.hover};
      border: 1px solid ${theme?.palette?.divider};
      padding: 1px 6px;
      border-radius: 5px;
      font-family: 'JetBrains Mono', 'Courier New', monospace;
      font-size: 0.85em;
    }

    pre {
      background-color: ${theme?.palette?.action?.hover};
      border: 1px solid ${theme?.palette?.divider};
      padding: 16px;
      border-radius: 10px;
      overflow-x: auto;
      margin: 0 0 18px;

      code {
        background-color: transparent;
        border: none;
        padding: 0;
        font-size: 13px;
      }
    }

    table {
      display: block;
      width: 100%;
      overflow-x: auto;
      border-collapse: collapse;
      font-size: 14.5px;
      margin: 22px 0;
    }

    th,
    td {
      text-align: left;
      padding: 11px 13px;
      border-bottom: 1px solid ${theme?.palette?.divider};
    }

    thead th {
      font-weight: 600;
      color: ${theme?.palette?.text?.primary};
    }

    td {
      color: ${theme?.palette?.text?.secondary};
    }
  `
});
var modalStyles = (theme) => ({
  markdownContent: css`
    color: ${theme?.palette?.text?.primary};
    line-height: 1.7;

    h1 {
      font-size: 2.5rem;
      font-weight: 700;
      margin-bottom: 12px;
      color: ${theme?.palette?.text?.primary};
      text-align: center;
    }

    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      margin-top: 40px;
      margin-bottom: 16px;
      color: ${theme?.palette?.text?.primary};
      padding-top: 16px;
    }

    h3 {
      font-size: 1.25rem;
      font-weight: 600;
      margin-top: 28px;
      margin-bottom: 12px;
      color: ${theme?.palette?.text?.primary};
    }

    p {
      margin-bottom: 16px;
      color: ${theme?.palette?.text?.secondary};
      font-size: 1rem;
    }

    ul, ol {
      margin-bottom: 16px;
      padding-left: 28px;
      color: ${theme?.palette?.text?.secondary};
    }

    li {
      margin-bottom: 10px;
      line-height: 1.6;
    }

    strong {
      font-weight: 600;
      color: ${theme?.palette?.text?.primary};
    }

    hr {
      margin: 32px 0;
      border: none;
      border-top: 2px solid ${theme?.palette?.divider};
    }

    code {
      background-color: ${theme?.palette?.action?.hover};
      padding: 2px 8px;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
    }

    pre {
      background-color: ${theme?.palette?.action?.hover};
      padding: 16px;
      border-radius: 8px;
      overflow-x: auto;
      margin-bottom: 16px;

      code {
        background-color: transparent;
        padding: 0;
      }
    }

    a {
      color: ${theme?.palette?.primary?.main};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    blockquote {
      border-left: 4px solid ${theme?.palette?.primary?.main};
      padding-left: 16px;
      margin: 16px 0;
      color: ${theme?.palette?.text?.secondary};
      font-style: italic;
    }
  `
});
var ContentfulMarkdown_default = ContentfulMarkdown;

// src/components/ModuleContents.tsx
import { useEffect as useEffect2, useState as useState2 } from "react";
import { Box as Box2, Link, Stack, Typography as Typography2 } from "@mui/material";
import { BiCheckCircle as BiCheckCircle2, BiFlag } from "react-icons/bi";

// src/components/ModuleCompletionFooter.tsx
import { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { BiCheck, BiCheckCircle, BiCircle } from "react-icons/bi";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var COMPLETION_ANCHOR_ID = "module-completion";
function formatCompletedAt(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(void 0, { year: "numeric", month: "short", day: "numeric" });
}
function ModuleCompletionFooter({ completed, completedAt, onToggle }) {
  const cardRef = useRef(null);
  const firstObservation = useRef(true);
  const pulseTimer = useRef(void 0);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (firstObservation.current) {
          firstObservation.current = false;
          return;
        }
        if (entry.isIntersecting) {
          setPulse(true);
          clearTimeout(pulseTimer.current);
          pulseTimer.current = setTimeout(() => setPulse(false), 1300);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      clearTimeout(pulseTimer.current);
    };
  }, []);
  const completedDate = formatCompletedAt(completedAt);
  return /* @__PURE__ */ jsx2(Box, { id: COMPLETION_ANCHOR_ID, sx: { mt: 5, pt: 3.5, borderTop: "1px solid", borderColor: "divider", scrollMarginTop: 88 }, children: /* @__PURE__ */ jsxs(
    Box,
    {
      ref: cardRef,
      sx: (theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        transition: "box-shadow .4s ease, background-color .3s, border-color .3s",
        boxShadow: pulse ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.3)}` : "none",
        bgcolor: completed ? alpha(theme.palette.success.main, 0.1) : theme.palette.action.hover,
        borderColor: completed ? alpha(theme.palette.success.main, 0.3) : theme.palette.divider
      }),
      children: [
        /* @__PURE__ */ jsx2(
          Box,
          {
            sx: {
              flex: "none",
              display: "inline-flex",
              color: completed ? "success.dark" : "text.disabled"
            },
            children: completed ? /* @__PURE__ */ jsx2(BiCheckCircle, { size: 28 }) : /* @__PURE__ */ jsx2(BiCircle, { size: 26 })
          }
        ),
        /* @__PURE__ */ jsxs(Box, { sx: { flex: "1 1 260px", minWidth: 0 }, children: [
          /* @__PURE__ */ jsx2(Typography, { sx: { fontWeight: 700, fontSize: 15.5, color: "text.primary" }, children: completed ? "Completed" : "Finished this training?" }),
          /* @__PURE__ */ jsx2(Typography, { variant: "body2", sx: { color: "text.secondary", mt: 0.3 }, children: completed ? `Marked complete${completedDate ? ` on ${completedDate}` : ""} \xB7 saved to your completed list.` : "Mark it complete to add it to your completed list \u2014 just for your records, and you can change it anytime." })
        ] }),
        completed ? /* @__PURE__ */ jsx2(
          Button,
          {
            variant: "text",
            size: "small",
            onClick: () => onToggle(false),
            sx: { flex: "none", color: "text.secondary", textTransform: "none", fontWeight: 600 },
            children: "Mark as not complete"
          }
        ) : /* @__PURE__ */ jsx2(
          Button,
          {
            variant: "contained",
            onClick: () => onToggle(true),
            startIcon: /* @__PURE__ */ jsx2(BiCheck, {}),
            sx: { flex: "none", textTransform: "none", fontWeight: 600 },
            children: "Mark as complete"
          }
        )
      ]
    }
  ) });
}

// src/components/ModuleContents.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function ModuleContents({
  headings,
  completed = false,
  // The internal all-access reader (admin ModuleReader) reuses this rail but has no completion
  // footer to point at, so it hides the "Finish" anchor. Defaults to true = unchanged dealer view.
  showFinish = true
}) {
  const [activeId, setActiveId] = useState2(headings[0]?.id);
  useEffect2(() => {
    const els = headings.map((h) => document.getElementById(h.id)).filter((el) => Boolean(el));
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-88px 0px -70% 0px", threshold: 0 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [headings]);
  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(id);
  };
  const handleFinishClick = (e) => {
    e.preventDefault();
    document.getElementById(COMPLETION_ANCHOR_ID)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  return (
    // Sticky positioning is applied by the parent grid item in ModuleReader (see note there),
    // so this node stays a plain content block.
    /* @__PURE__ */ jsxs2(Box2, { component: "nav", "aria-label": "On this page", children: [
      /* @__PURE__ */ jsx3(Typography2, { variant: "overline", sx: { display: "block", color: "text.disabled", pl: 1.25, mb: 1 }, children: "On this page" }),
      /* @__PURE__ */ jsx3(Stack, { spacing: 0, children: headings.map((h) => {
        const active = activeId === h.id;
        return /* @__PURE__ */ jsx3(
          Link,
          {
            href: `#${h.id}`,
            onClick: (e) => handleClick(e, h.id),
            underline: "none",
            sx: {
              display: "block",
              py: 0.6,
              pl: h.level >= 3 ? 2.75 : 1.25,
              fontSize: h.level >= 3 ? 12.5 : 13,
              lineHeight: 1.35,
              borderLeft: "2px solid",
              borderColor: active ? "primary.main" : "divider",
              color: active ? "primary.main" : "text.secondary",
              fontWeight: active ? 600 : 400,
              transition: "color .12s, border-color .12s",
              "&:hover": { color: "text.primary" }
            },
            children: h.text
          },
          h.id
        );
      }) }),
      showFinish && /* @__PURE__ */ jsx3(Box2, { sx: { mt: 1.5, pt: 1.5, borderTop: "1px dashed", borderColor: "divider" }, children: /* @__PURE__ */ jsxs2(
        Link,
        {
          href: `#${COMPLETION_ANCHOR_ID}`,
          onClick: handleFinishClick,
          underline: "none",
          sx: {
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: 1.25,
            py: 0.6,
            fontSize: 13,
            fontWeight: 600,
            color: completed ? "success.dark" : "text.secondary",
            transition: "color .12s",
            "&:hover": { color: "text.primary" }
          },
          children: [
            completed ? /* @__PURE__ */ jsx3(BiCheckCircle2, { size: 16 }) : /* @__PURE__ */ jsx3(BiFlag, { size: 15 }),
            completed ? "Completed" : "Finish"
          ]
        }
      ) })
    ] })
  );
}

// src/components/ModuleVideo.tsx
import { Box as Box3, Typography as Typography3 } from "@mui/material";

// src/lib/youtube.ts
function extractYouTubeId(text) {
  if (!text) return null;
  const re = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = text.match(re);
  return match ? match[1] : null;
}

// src/components/ModuleVideo.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
function ModuleVideo({ url }) {
  const videoId = extractYouTubeId(url);
  if (!videoId) {
    return /* @__PURE__ */ jsx4(Box3, { sx: { borderRadius: 1, bgcolor: "action.hover", p: 3, textAlign: "center", color: "text.secondary" }, children: /* @__PURE__ */ jsx4(Typography3, { variant: "body2", children: "No video" }) });
  }
  return /* @__PURE__ */ jsx4(
    Box3,
    {
      sx: {
        position: "relative",
        width: "100%",
        paddingTop: "56.25%",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "common.black"
      },
      children: /* @__PURE__ */ jsx4(
        Box3,
        {
          component: "iframe",
          src: `https://www.youtube.com/embed/${videoId}?rel=0`,
          title: "Learning module video",
          allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
          allowFullScreen: true,
          sx: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }
        }
      )
    }
  );
}

// src/hooks/useResolvedMarkdown.ts
import { useEffect as useEffect3, useMemo as useMemo2, useRef as useRef2, useState as useState3 } from "react";
var PRIVATE_URL_SCAN = /https?:\/\/(?:s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com\/)?documents(?:-[a-z0-9]+)?\.hopdrive\.com\/(?:[^\s()"<>]|\([^\s()"<>]*\))+/gi;
function findPrivateAssetUrls(source) {
  const matches = source.match(PRIVATE_URL_SCAN) ?? [];
  return Array.from(new Set(matches.map((u) => u.replace(/'+$/, ""))));
}
var HARD_EXPIRY_MS = 5 * 6e4;
var REFRESH_AFTER_MS = 4 * 6e4;
var RETRY_AFTER_MS = 3e4;
var escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
function substituteUrls(source, pairs) {
  if (pairs.length === 0) return source;
  const bySource = new Map(pairs);
  const pattern = pairs.map(([orig]) => orig).sort((a, b) => b.length - a.length).map(escapeRegExp).join("|");
  return source.replace(new RegExp(pattern, "g"), (m) => bySource.get(m) ?? m);
}
function useResolvedMarkdown(content, active, resolveAssetUrl) {
  const source = content ?? "";
  const [batch, setBatch] = useState3(null);
  const batchRef = useRef2(null);
  const lastAttemptRef = useRef2(0);
  const resolveRef = useRef2(resolveAssetUrl);
  resolveRef.current = resolveAssetUrl;
  const urls = useMemo2(() => findPrivateAssetUrls(source), [source]);
  const needsResolution = urls.length > 0;
  useEffect3(() => {
    if (urls.length === 0) return;
    const current = batchRef.current?.source === source ? batchRef.current : null;
    const setBoth = (b) => {
      batchRef.current = b;
      setBatch(b);
    };
    if (!active) {
      if (!current || current.at === 0) return;
      const remaining = current.at + HARD_EXPIRY_MS - Date.now();
      if (remaining <= 0) {
        setBoth(null);
        return;
      }
      const expiryTimer = setTimeout(() => setBoth(null), remaining);
      return () => clearTimeout(expiryTimer);
    }
    let cancelled = false;
    let timer;
    const resolveBatch = async () => {
      lastAttemptRef.current = Date.now();
      const settled = await Promise.allSettled(
        urls.map(async (u) => [u, await resolveRef.current(u)])
      );
      if (cancelled) return;
      const pairs = settled.filter((r) => r.status === "fulfilled").map((r) => r.value).filter(([, signed]) => Boolean(signed));
      if (pairs.length > 0) {
        setBoth({
          source,
          markdown: substituteUrls(source, pairs),
          at: Date.now(),
          complete: pairs.length === urls.length
        });
      } else if (current && current.at > 0) {
        setBoth({ ...current, complete: false });
      } else {
        setBoth({ source, markdown: source, at: 0, complete: false });
      }
    };
    const runIn = (ms) => {
      if (ms <= 0) void resolveBatch();
      else timer = setTimeout(() => void resolveBatch(), ms);
    };
    const now = Date.now();
    if (!current) {
      void resolveBatch();
    } else if (now - current.at >= HARD_EXPIRY_MS && current.at > 0) {
      setBoth(null);
    } else if (current.at === 0 || !current.complete) {
      runIn(lastAttemptRef.current + RETRY_AFTER_MS - now);
    } else {
      runIn(current.at + REFRESH_AFTER_MS - now);
    }
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [source, active, urls, batch]);
  const isCurrent = batch?.source === source;
  return {
    markdown: !needsResolution ? source : isCurrent ? batch.markdown : "",
    resolving: needsResolution && !isCurrent
  };
}

// src/types.ts
var FEATURE_LABELS = {
  generic: "General",
  move: "Moves",
  mobile_service: "Mobile Service",
  guest_ride: "Guest Rides",
  appraisals: "Appraisals",
  appointments: "Appointments"
};
var featureLabel = (f) => FEATURE_LABELS[f] ?? f;

// src/components/ModuleReader.tsx
import { Fragment, jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var TOC_MIN_SECTIONS = 3;
function FileRow({ title, subtitle, action }) {
  return /* @__PURE__ */ jsxs3(
    Stack2,
    {
      direction: "row",
      alignItems: "center",
      spacing: 1.5,
      sx: { p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 },
      children: [
        /* @__PURE__ */ jsx5(Box4, { sx: { color: "secondary.main", display: "inline-flex" }, children: /* @__PURE__ */ jsx5(BiFile, { size: 26 }) }),
        /* @__PURE__ */ jsxs3(Box4, { sx: { flexGrow: 1, minWidth: 0 }, children: [
          /* @__PURE__ */ jsx5(Typography4, { sx: { fontWeight: 600 }, children: title }),
          /* @__PURE__ */ jsx5(Typography4, { variant: "caption", color: "text.secondary", children: subtitle })
        ] }),
        action
      ]
    }
  );
}
function ModuleReader({
  module,
  resolveAssetUrl,
  onDownloadPdf,
  completion,
  renderVideo,
  // No handler is worse than a loud one: a swallowed presign failure looks like a dead button.
  onError = (message) => console.error(message)
}) {
  const isDriverModule = module.audiences.includes("driver");
  const [downloading, setDownloading] = useState4(false);
  const [pdfLoading, setPdfLoading] = useState4(false);
  const showCompletion = Boolean(completion);
  const completed = completion?.completed ?? false;
  const handleVideoEnded = useCallback(() => {
    if (completion && !completion.completed) completion.onToggle(true);
  }, [completion]);
  const hasVideo = Boolean(extractYouTubeId(module.hero_video_url));
  const hasContent = Boolean(module.content?.trim());
  const hasFile = Boolean(module.attachment_url);
  const tabs = useMemo3(() => {
    const list = [];
    if (hasVideo) list.push({ id: "watch", label: "Watch", icon: /* @__PURE__ */ jsx5(BiPlayCircle, { size: 17 }) });
    if (hasContent) list.push({ id: "read", label: "Read", icon: /* @__PURE__ */ jsx5(BiBookOpen, { size: 17 }) });
    if (hasContent || hasFile) list.push({ id: "files", label: "Files", icon: /* @__PURE__ */ jsx5(BiFile, { size: 16 }) });
    return list;
  }, [hasVideo, hasContent, hasFile]);
  const [selected, setSelected] = useState4(() => tabs[0]?.id);
  const active = tabs.some((t) => t.id === selected) ? selected : tabs[0]?.id;
  const { markdown, resolving } = useResolvedMarkdown(module.content, active === "read", resolveAssetUrl);
  const { html, headings } = useMemo3(() => renderMarkdown(markdown), [markdown]);
  const showToc = headings.filter((h) => h.level === 2).length >= TOC_MIN_SECTIONS;
  const handleDownload = async () => {
    if (!module.attachment_url) return;
    setDownloading(true);
    const win = window.open("", "_blank");
    if (win) win.opener = null;
    try {
      const url = await resolveAssetUrl(module.attachment_url);
      if (!url) throw new Error("attachment URL did not resolve");
      if (win) {
        win.location.href = url;
      } else {
        const fallback = window.open(url, "_blank");
        if (fallback) fallback.opener = null;
        else onError("Your browser blocked the document tab \u2014 allow pop-ups for this site and try again.");
      }
    } catch {
      win?.close();
      onError("Couldn\u2019t open the training document. Please try again.");
    } finally {
      setDownloading(false);
    }
  };
  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      await onDownloadPdf();
    } catch {
      onError("Couldn\u2019t generate the training PDF. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };
  return /* @__PURE__ */ jsxs3(Box4, { children: [
    /* @__PURE__ */ jsxs3(
      Stack2,
      {
        direction: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "flex-start" },
        spacing: 1.5,
        children: [
          /* @__PURE__ */ jsxs3(Box4, { sx: { minWidth: 0 }, children: [
            /* @__PURE__ */ jsxs3(Stack2, { direction: "row", spacing: 1, sx: { mb: 1, flexWrap: "wrap", gap: 0.5 }, children: [
              /* @__PURE__ */ jsx5(Chip, { size: "small", label: featureLabel(module.feature), sx: { fontWeight: 600 } }),
              /* @__PURE__ */ jsx5(Chip, { size: "small", variant: "outlined", label: module.tiers ? module.tiers.join(", ") : "all tiers" }),
              isDriverModule && module.driver_types && /* @__PURE__ */ jsx5(Chip, { size: "small", variant: "outlined", label: module.driver_types.join(", ") })
            ] }),
            /* @__PURE__ */ jsx5(Typography4, { variant: "h5", sx: { fontWeight: 700 }, children: module.name }),
            module.description && /* @__PURE__ */ jsx5(Typography4, { variant: "body2", color: "text.secondary", sx: { mt: 0.5 }, children: module.description })
          ] }),
          completed && /* @__PURE__ */ jsx5(
            Chip,
            {
              icon: /* @__PURE__ */ jsx5(BiCheckCircle3, {}),
              label: "Completed",
              color: "success",
              variant: "outlined",
              sx: { flexShrink: 0, fontWeight: 600 }
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx5(Divider, { sx: { mt: 2 } }),
    tabs.length === 0 ? /* @__PURE__ */ jsx5(Typography4, { variant: "body2", color: "text.secondary", sx: { py: 4, textAlign: "center" }, children: "No materials for this training yet." }) : /* @__PURE__ */ jsxs3(Fragment, { children: [
      /* @__PURE__ */ jsx5(
        Tabs,
        {
          value: active,
          onChange: (_, v) => setSelected(v),
          sx: { mb: 2, minHeight: 44, "& .MuiTab-root": { minHeight: 44, textTransform: "none", fontWeight: 600 } },
          children: tabs.map((t) => /* @__PURE__ */ jsx5(Tab, { value: t.id, label: t.label, icon: t.icon, iconPosition: "start" }, t.id))
        }
      ),
      active === "watch" && (renderVideo ? renderVideo({ url: module.hero_video_url, onEnded: handleVideoEnded }) : /* @__PURE__ */ jsx5(ModuleVideo, { url: module.hero_video_url })),
      active === "read" && (resolving ? /* @__PURE__ */ jsx5(Box4, { sx: { display: "flex", justifyContent: "center", py: 4 }, children: /* @__PURE__ */ jsx5(CircularProgress, { size: 24 }) }) : showToc ? /* @__PURE__ */ jsxs3(
        Box4,
        {
          sx: {
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "200px minmax(0, 1fr)" },
            columnGap: { md: 5 },
            alignItems: "start"
          },
          children: [
            /* @__PURE__ */ jsx5(Box4, { sx: { display: { xs: "none", md: "block" }, position: "sticky", top: 88, alignSelf: "start" }, children: /* @__PURE__ */ jsx5(ModuleContents, { headings, completed, showFinish: showCompletion }) }),
            /* @__PURE__ */ jsxs3(Box4, { sx: { minWidth: 0 }, children: [
              /* @__PURE__ */ jsx5(ContentfulMarkdown_default, { html, variant: "article" }),
              completion && /* @__PURE__ */ jsx5(
                ModuleCompletionFooter,
                {
                  completed,
                  completedAt: completion.completedAt,
                  onToggle: completion.onToggle
                }
              )
            ] })
          ]
        }
      ) : /* @__PURE__ */ jsxs3(Box4, { sx: { maxWidth: "72ch", mx: "auto" }, children: [
        /* @__PURE__ */ jsx5(ContentfulMarkdown_default, { html, variant: "article" }),
        completion && /* @__PURE__ */ jsx5(
          ModuleCompletionFooter,
          {
            completed,
            completedAt: completion.completedAt,
            onToggle: completion.onToggle
          }
        )
      ] })),
      active === "files" && /* @__PURE__ */ jsxs3(Stack2, { spacing: 1.5, children: [
        /* @__PURE__ */ jsx5(
          FileRow,
          {
            title: "Training PDF",
            subtitle: "Generated from this training",
            action: /* @__PURE__ */ jsx5(
              Button2,
              {
                onClick: handleDownloadPdf,
                disabled: pdfLoading,
                variant: "outlined",
                startIcon: pdfLoading ? /* @__PURE__ */ jsx5(CircularProgress, { size: 16 }) : /* @__PURE__ */ jsx5(BiDownload, {}),
                sx: { textTransform: "none", flexShrink: 0 },
                children: "Download PDF"
              }
            )
          }
        ),
        hasFile && /* @__PURE__ */ jsx5(
          FileRow,
          {
            title: "Attached document",
            subtitle: "Opens in a new tab",
            action: /* @__PURE__ */ jsx5(
              Button2,
              {
                onClick: handleDownload,
                disabled: downloading,
                variant: "outlined",
                startIcon: downloading ? /* @__PURE__ */ jsx5(CircularProgress, { size: 16 }) : /* @__PURE__ */ jsx5(BiDownload, {}),
                sx: { textTransform: "none", flexShrink: 0 },
                children: "Download"
              }
            )
          }
        )
      ] })
    ] })
  ] });
}
export {
  COMPLETION_ANCHOR_ID,
  ContentfulMarkdown_default as ContentfulMarkdown,
  ModuleCompletionFooter,
  ModuleContents,
  ModuleReader,
  ModuleVideo,
  featureLabel,
  findPrivateAssetUrls,
  renderMarkdown,
  substituteUrls,
  useResolvedMarkdown
};
//# sourceMappingURL=index.mjs.map